import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles, Calendar, Users } from "lucide-react";
import ProfileAvatar from "./ProfileAvatar";
import ProfileMediaGallery from "./ProfileMediaGallery";
import { ProfileViewModal } from "./profile/ProfileViewModal";
import { useProfileViewer } from "../hooks/useProfileViewer";
import apiClient from "../api/client";
import { markFlowStep } from '../utils/unityFlowStorage';
import { track } from '../utils/analytics';
import { safeArr } from '../utils/safeProfile';

// Session storage key for persisting position
const QUICKMATCH_INDEX_KEY = "unitylove_quickmatch_index_v1";

export default function Discover() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = localStorage.getItem("token");
  
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [index, setIndex] = useState(0);
  const [likeLoading, setLikeLoading] = useState(false);
  const [passLoading, setPassLoading] = useState(false);
  
  // End-of-deck state - true when user has swiped past last profile
  const [reachedEnd, setReachedEnd] = useState(false);
  
  // Profile viewer modal
  const profileViewer = useProfileViewer();
  
  // Session tracking for completion logic
  const [sessionFlirtsSent, setSessionFlirtsSent] = useState(0);
  const [sessionPasses, setSessionPasses] = useState(0);

  // Relationship profile completeness check for onboarding nudge
  const [relationshipProfile, setRelationshipProfile] = useState(null);
  const [loadingRelProfile, setLoadingRelProfile] = useState(true);
  const [relProfileError, setRelProfileError] = useState(null);

  // Fetch relationship profile to check completeness
  useEffect(() => {
    let isMounted = true;

    async function fetchRelationshipProfile() {
      try {
        const res = await apiClient.get("/relationship/profile");
        const data = res.data;
        if (isMounted) {
          setRelationshipProfile(data);
          setLoadingRelProfile(false);
        }
      } catch (err) {
        if (isMounted) {
          console.error("[Discover] Relationship profile fetch error:", err);
          setRelProfileError(err);
          setLoadingRelProfile(false);
        }
      }
    }

    fetchRelationshipProfile();
    return () => {
      isMounted = false;
    };
  }, []);

  const isRelationshipProfileIncomplete =
    !loadingRelProfile &&
    relationshipProfile &&
    (
      !relationshipProfile.relationship_style ||
      !Array.isArray(relationshipProfile.relationship_goals) ||
      relationshipProfile.relationship_goals.length === 0
    );

  // Fetch profiles once on mount, restore index from storage/URL
  useEffect(() => {
    let cancelled = false;

    async function loadProfiles() {
      try {
        setLoading(true);
        setLoadError(null);

        console.info("[Discover] Fetching profiles from /api/profiles/discover...");

        const res = await apiClient.get("/profiles/discover");
        const data = res.data;
        const items = Array.isArray(data) ? data : [];

        if (!Array.isArray(data)) {
          console.warn("[Discover] Non-array response from /api/profiles/discover:", data);
        } else {
          console.info(`[Discover] Loaded ${items.length} profiles`);
        }

        if (!cancelled) {
          setProfiles(items);
          
          // Restore index from URL param or sessionStorage
          const urlIndex = searchParams.get('i');
          const storedIndex = sessionStorage.getItem(QUICKMATCH_INDEX_KEY);
          
          let restoredIndex = 0;
          if (urlIndex && !isNaN(parseInt(urlIndex, 10))) {
            restoredIndex = parseInt(urlIndex, 10);
          } else if (storedIndex && !isNaN(parseInt(storedIndex, 10))) {
            restoredIndex = parseInt(storedIndex, 10);
          }
          
          // Clamp to valid range
          const maxIndex = items.length > 0 ? items.length - 1 : 0;
          const clampedIndex = Math.min(Math.max(0, restoredIndex), maxIndex);
          
          console.info(`[Discover] Restored index: ${clampedIndex} (url: ${urlIndex}, stored: ${storedIndex}, max: ${maxIndex})`);
          setIndex(clampedIndex);
          
          // Check if we've already reached the end
          if (restoredIndex >= items.length && items.length > 0) {
            setReachedEnd(true);
          }
        }
      } catch (err) {
        console.error("[Discover] Failed to load profiles:", err);
        if (!cancelled) {
          setLoadError("failed");
          setProfiles([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadProfiles();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const hasProfiles = Array.isArray(profiles) && profiles.length > 0;
  const safeIndex =
    typeof index === "number" && index >= 0 && index < (profiles?.length || 0)
      ? index
      : 0;
  const current = hasProfiles && !reachedEnd ? profiles[safeIndex] : null;

  // Persist index to sessionStorage whenever it changes
  useEffect(() => {
    if (typeof index === 'number' && index >= 0) {
      sessionStorage.setItem(QUICKMATCH_INDEX_KEY, String(index));
      // Also update URL param for shareable position
      setSearchParams(prev => {
        prev.set('i', String(index));
        return prev;
      }, { replace: true });
    }
  }, [index, setSearchParams]);

  // Extremely defensive field extraction
  const profileUserId =
    (current && (current.user_id || current.id || current.userId)) || null;

  const profileDisplayName =
    (current &&
      (current.display_name ||
        current.name ||
        current.username ||
        current?.profile?.display_name)) ||
    "Someone interesting";

  const profileAge =
    (current && (current.age || current?.profile?.age)) || null;

  const profileLocation =
    (current &&
      (current.location_city ||
        current.city ||
        current?.profile?.location_city)) ||
    null;

  // Distance in miles (populated by backend discovery endpoint)
  const profileDistanceMiles =
    (current &&
      (current.distance_miles_away ??
        current.distanceMilesAway ??
        current?.profile?.distance_miles_away)) ??
    null;

  const distanceLabel =
    typeof profileDistanceMiles === "number" && Number.isFinite(profileDistanceMiles)
      ? profileDistanceMiles >= 100
        ? "100+ miles away"
        : `${profileDistanceMiles < 10 ? profileDistanceMiles.toFixed(1) : Math.round(profileDistanceMiles)} miles away`
      : null;

  const profileGender =
    (current &&
      (current.gender || current?.profile?.gender)) ||
    null;

  // Intent Signal v1 - extract safely
  const profileIntentSignal =
    (current &&
      (current.intent_signal || current?.profile?.intent_signal)) ||
    null;

  // Intent Signal display label mapping
  const INTENT_SIGNAL_LABELS = {
    serious: "Serious",
    friends_first: "Friends-first",
    casual: "Casual",
    exploring: "Exploring",
  };

  const profileBio =
    (current &&
      (current.bio || current?.profile?.bio)) ||
    null;

  // Navigation helpers
  const handleNext = useCallback(() => {
    setIndex((prev) => {
      const total = profiles?.length || 0;
      if (total === 0) return 0;
      const next = prev + 1;
      return next >= total ? total - 1 : next;
    });
  }, [profiles]);

  const handlePrev = useCallback(() => {
    setIndex((prev) => {
      const total = profiles?.length || 0;
      if (total === 0) return 0;
      const next = prev - 1;
      return next < 0 ? 0 : next;
    });
  }, [profiles]);

  const handleLike = useCallback(async () => {
    if (!profileUserId || likeLoading) return;

    try {
      setLikeLoading(true);
      console.info("[Discover] Sending LIKE for user:", profileUserId);

      const res = await apiClient.post(`/matches/like/${encodeURIComponent(profileUserId)}`);

      console.info("[Discover] Like successful for:", profileUserId);
      setSessionFlirtsSent(prev => prev + 1);  // Track interaction
      
      // 🔥 Mark Unity Flow "Send a flirt or like" task complete
      markFlowStep('sentFlirt');

      // On success or failure, move to next profile or reach end
      const total = profiles?.length || 0;
      const nextIndex = index + 1;
      
      if (nextIndex >= total) {
        // Reached the end of the deck
        setReachedEnd(true);
        console.info("[Discover] Reached end of deck after like");
      } else {
        setIndex(nextIndex);
      }
    } catch (err) {
      console.error("[Discover] handleLike error:", err);
    } finally {
      setLikeLoading(false);
    }
  }, [profileUserId, likeLoading, profiles, index]);

  const handlePass = useCallback(async () => {
    if (!profileUserId || passLoading) return;

    try {
      setPassLoading(true);
      console.info("[Discover] Sending PASS for user:", profileUserId);

      await apiClient.post(`/matches/pass/${encodeURIComponent(profileUserId)}`);

      console.info("[Discover] Pass successful for:", profileUserId);
      setSessionPasses(prev => prev + 1);  // Track interaction
      
      // 🔥 Mark Unity Flow "Send a flirt or like" task complete (pass counts too!)
      markFlowStep('sentFlirt');

      // On success or failure, move to next profile or reach end
      const total = profiles?.length || 0;
      const nextIndex = index + 1;
      
      if (nextIndex >= total) {
        // Reached the end of the deck
        setReachedEnd(true);
        console.info("[Discover] Reached end of deck after pass");
      } else {
        setIndex(nextIndex);
      }
    } catch (err) {
      console.error("[Discover] handlePass error:", err);
    } finally {
      setPassLoading(false);
    }
  }, [profileUserId, passLoading, profiles, index]);

  // Simple debug log for the current profile
  useEffect(() => {
    if (current && profileUserId) {
      console.info("[Discover] Showing profile:", {
        user_id: profileUserId,
        display_name: profileDisplayName,
      });
    } else if (!loading && hasProfiles) {
      console.warn("[Discover] Current profile is missing user_id or data:", current);
    }
  }, [current, profileUserId, profileDisplayName, loading, hasProfiles]);

  // Error state UI
  if (loadError === "failed") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="flex flex-col items-center gap-4 text-center max-w-md">
          <div className="w-16 h-16 rounded-full flex items-center justify-center bg-red-900/40 border border-red-700/50">
            <span className="text-red-400 text-xl">!</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-50">
            Couldn't load Quick Match
          </h2>
          <p className="text-slate-400 text-sm">
            We couldn't load matches right now. Please try again in a moment.
          </p>
          <p className="text-xs text-slate-500">
            Check the browser console for logs starting with{" "}
            <code>[Discover]</code> for more details.
          </p>
        </div>
      </div>
    );
  }

  // Loading state UI
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="w-12 h-12 rounded-full border-2 border-slate-700 border-t-slate-300 animate-spin" />
          <p className="text-slate-300 text-sm font-medium">
            Finding your next connection…
          </p>
        </div>
      </div>
    );
  }

  // No profiles available - Different states for initial empty vs swiped through all
  if (!hasProfiles) {
    const hasUserInteracted = sessionFlirtsSent > 0 || sessionPasses > 0;
    console.info("[Discover] No profiles to show.", hasUserInteracted ? "User swiped through all." : "Backend returned empty.");

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 py-6">
        <div className="w-full max-w-md">
          {/* Header - Back Button */}
          <button
            onClick={() => {
              // Mark Quick Match as complete
              if (token) {
                apiClient.post(`/users/complete-quick-match`, {}).catch(err => console.error('[Discover] Failed to mark complete:', err));
              }
              navigate('/dashboard');
            }}
            className="flex items-center gap-2 text-indigo-300 hover:text-indigo-200 transition-colors text-sm font-medium mb-8"
            style={{ fontSize: '15px' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>

          {/* Main Card */}
          <div 
            className="w-full rounded-3xl p-7 flex flex-col items-center text-center"
            style={{
              background: '#0F1120',
              border: '1px solid rgba(255,255,255,0.05)',
              boxShadow: '0 0 20px rgba(168,85,247,0.15)'
            }}
          >
            {/* Icon - Different for each state */}
            <div className="text-5xl mb-6 opacity-85">
              {hasUserInteracted ? '🎉' : '🌙'}
            </div>

            {/* Title - Different for each state */}
            <h2 
              className="font-bold text-white mb-2"
              style={{
                fontSize: '22px',
                lineHeight: '130%',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              {hasUserInteracted ? "You've seen everyone for now!" : "No new profiles right now"}
            </h2>

            {/* Subtitle - Different for each state */}
            <p 
              className="font-medium mb-7"
              style={{
                fontSize: '15px',
                lineHeight: '150%',
                color: 'rgba(255,255,255,0.65)',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              {hasUserInteracted ? (
                <>
                  New profiles drop daily — check back soon!
                </>
              ) : (
                <>
                  Check back later for new profiles.<br />
                  New members join every day.
                </>
              )}
            </p>

            {/* Primary Button - Return to Dashboard */}
            <button
              type="button"
              onClick={() => {
                // Mark Quick Match as complete
                if (token) {
                  apiClient.post(`/users/complete-quick-match`, {}).catch(err => console.error('[Discover] Failed to mark complete:', err));
                }
                navigate('/dashboard');
              }}
              className="w-full font-semibold text-white transition-all hover:scale-[1.02] mb-3"
              style={{
                background: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
                borderRadius: '50px',
                padding: '14px',
                fontSize: '16px',
                boxShadow: '0 0 16px rgba(220,38,38,0.25)',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              Return to Dashboard
            </button>

            {/* Secondary Button - Visit Lifestyle Lounge */}
            <button
              type="button"
              onClick={() => {
                // Mark Quick Match as complete
                if (token) {
                  apiClient.post(`/users/complete-quick-match`, {}).catch(err => console.error('[Discover] Failed to mark complete:', err));
                }
                navigate('/lifestyle-lounge');
              }}
              className="w-full font-medium transition-all hover:bg-purple-500/10 mb-4"
              style={{
                border: '1.5px solid #A78BFA',
                background: 'transparent',
                borderRadius: '50px',
                padding: '12px',
                fontSize: '15px',
                color: '#C4B5FD',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              Visit the Lifestyle Lounge
            </button>

            {/* Tertiary Link - View Profiles Again */}
            <button
              type="button"
              onClick={() => {
                // Reload profiles (reset session)
                setIndex(0);
                setProfiles([]);
                setLoading(true);
                setSessionFlirtsSent(0);
                setSessionPasses(0);
                // Re-fetch will happen via useEffect
              }}
              className="font-medium underline-offset-4 hover:underline transition-colors"
              style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.4)',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              View Profiles Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // End-of-deck state - user swiped through all profiles
  if (reachedEnd) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 py-6">
        <div className="w-full max-w-md">
          {/* Header - Back Button */}
          <button
            onClick={() => {
              if (token) {
                apiClient.post(`/users/complete-quick-match`, {}).catch(err => console.error('[Discover] Failed to mark complete:', err));
              }
              // Clear stored index on exit
              sessionStorage.removeItem(QUICKMATCH_INDEX_KEY);
              navigate('/dashboard');
            }}
            className="flex items-center gap-2 text-indigo-300 hover:text-indigo-200 transition-colors text-sm font-medium mb-8"
            style={{ fontSize: '15px' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>

          {/* End of Deck Card */}
          <div 
            className="w-full rounded-3xl p-7 flex flex-col items-center text-center"
            style={{
              background: '#0F1120',
              border: '1px solid rgba(255,255,255,0.05)',
              boxShadow: '0 0 20px rgba(168,85,247,0.15)'
            }}
          >
            <div className="text-5xl mb-6 opacity-85">🎉</div>

            <h2 
              className="font-bold text-white mb-2"
              style={{
                fontSize: '22px',
                lineHeight: '130%',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              You&apos;re all caught up!
            </h2>

            <p 
              className="font-medium mb-7"
              style={{
                fontSize: '15px',
                lineHeight: '150%',
                color: 'rgba(255,255,255,0.65)',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              You&apos;ve seen all {profiles.length} profiles. New members join daily — check back soon!
            </p>

            {/* Stats */}
            <div className="flex justify-center gap-6 mb-6 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-pink-400">{sessionFlirtsSent}</div>
                <div className="text-slate-500 text-xs">Flirts Sent</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-400">{sessionPasses}</div>
                <div className="text-slate-500 text-xs">Passed</div>
              </div>
            </div>

            {/* Primary Button - Return to Dashboard */}
            <button
              type="button"
              onClick={() => {
                if (token) {
                  apiClient.post(`/users/complete-quick-match`, {}).catch(err => console.error('[Discover] Failed to mark complete:', err));
                }
                sessionStorage.removeItem(QUICKMATCH_INDEX_KEY);
                navigate('/dashboard');
              }}
              className="w-full font-semibold text-white transition-all hover:scale-[1.02] mb-3"
              style={{
                background: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
                borderRadius: '50px',
                padding: '14px',
                fontSize: '16px',
                boxShadow: '0 0 16px rgba(220,38,38,0.25)',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              Return to Dashboard
            </button>

            {/* Check Events */}
            <button
              type="button"
              onClick={() => navigate('/events')}
              className="w-full font-medium transition-all hover:bg-purple-500/10 mb-3 flex items-center justify-center gap-2"
              style={{
                border: '1.5px solid #A78BFA',
                background: 'transparent',
                borderRadius: '50px',
                padding: '12px',
                fontSize: '15px',
                color: '#C4B5FD',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              <Calendar className="w-4 h-4" />
              Check Upcoming Events
            </button>

            {/* Visit Lifestyle Lounge */}
            <button
              type="button"
              onClick={() => navigate('/lifestyle-lounge')}
              className="w-full font-medium transition-all hover:bg-emerald-500/10 mb-4 flex items-center justify-center gap-2"
              style={{
                border: '1.5px solid #34D399',
                background: 'transparent',
                borderRadius: '50px',
                padding: '12px',
                fontSize: '15px',
                color: '#6EE7B7',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              <Users className="w-4 h-4" />
              Visit Lifestyle Lounge
            </button>

            {/* View Again Link */}
            <button
              type="button"
              onClick={() => {
                setReachedEnd(false);
                setIndex(0);
                sessionStorage.setItem(QUICKMATCH_INDEX_KEY, '0');
              }}
              className="font-medium underline-offset-4 hover:underline transition-colors"
              style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.4)',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              View Profiles Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main profile card UI
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 py-6">
      <div className="w-full max-w-md space-y-4">
        {/* Back to Dashboard Button */}
        <button
          onClick={() => {
            // Mark as completed if user interacted with at least 1 profile
            const sessionInteractions = sessionFlirtsSent + sessionPasses;
            if (sessionInteractions > 0 && token) {
              // Mark quick match as completed
              apiClient.post(`/users/complete-quick-match`, {}).catch(err => console.error('[Discover] Failed to mark complete:', err));
            }
            navigate('/dashboard');
          }}
          className="flex items-center gap-2 text-slate-300 hover:text-slate-100 transition-colors text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Onboarding nudge banner */}
        {!loadingRelProfile && !relProfileError && isRelationshipProfileIncomplete && (
          <div className="rounded-2xl border border-rose-700/50 bg-rose-900/20 px-4 py-3 flex items-start gap-3">
            <div className="mt-0.5">💜</div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-rose-200">
                Get higher-quality matches
              </p>
              <p className="text-xs text-rose-300/80 mt-0.5">
                Complete your relationship style and goals so we can match you with people
                who want the same structure.
              </p>
              <button
                type="button"
                onClick={() => navigate("/profile/edit")}
                className="mt-2 inline-flex items-center rounded-full bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-500 transition"
              >
                Update relationship profile
              </button>
            </div>
          </div>
        )}

        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 flex flex-col gap-4">
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <span className="text-xs uppercase tracking-wide text-slate-400">
                Quick Match
              </span>
              <span className="text-sm text-slate-500">
                Profile {safeIndex + 1} of {profiles.length}
              </span>
            </div>
          </div>

        <div className="mt-2 rounded-xl bg-slate-900/80 border border-slate-800 p-4 flex flex-col gap-2">
          {/* Tappable Profile Card - Click to view full profile */}
          <div 
            className="cursor-pointer hover:opacity-90 transition-opacity active:opacity-80"
            onClick={(e) => {
              // Prevent action if clicking on nested interactive elements
              if (e.target.closest('button') || e.target.closest('a')) return;
              
              // Open ProfileViewModal instead of navigating away
              if (current) {
                profileViewer.showProfile({
                  display_name: profileDisplayName,
                  age: profileAge,
                  gender: profileGender,
                  relationship_style: current.relationship_style,
                  looking_for: current.looking_for || [],
                  location_city: profileLocation,
                  location_region: current.location_region,
                  bio: current.bio,
                  interests: current.interests || [],
                  photos: current.profile_photos?.map(p => p.url || p) || current.photos || [],
                  profile_photos: current.profile_photos || current.photos || [],
                  avatar_url: current.avatar_url,
                  video_intro_url: current.video_intro_url || current.video_url,
                  type: current.type,
                  user_type: current.user_type || current.type,
                  background: current.background,
                  identity_verified: current.identity_verified || current.is_verified,
                  is_verified: current.is_verified || current.identity_verified,
                  entitlements: current.entitlements,
                  is_inner_circle: current.is_inner_circle,
                  // Intent Signal v1
                  intent_signal: profileIntentSignal,
                });
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`View full profile of ${profileDisplayName}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && current) {
                profileViewer.showProfile({
                  display_name: profileDisplayName,
                  age: profileAge,
                  gender: profileGender,
                  photos: current.profile_photos?.map(p => p.url || p) || current.photos || [],
                  bio: current.bio,
                  interests: current.interests || [],
                });
              }
            }}
          >
            {/* Media Gallery - Photos + Video */}
            <div className="mb-3">
              <ProfileMediaGallery
                photos={
                  current?.profile_photos?.map(p => p.url || p) 
                  || current?.photos 
                  || (current?.avatar_url ? [current.avatar_url] : [])
                }
                videoUrl={current?.video_intro_url || current?.video_url}
                displayName={profileDisplayName}
                size="lg"
              />
            </div>

            <h2 className="text-xl font-semibold text-slate-50">
              {profileDisplayName}
              {profileAge ? (
                <span className="text-slate-400 text-base">, {profileAge}</span>
              ) : null}
            </h2>

            {profileLocation || distanceLabel ? (
              <p className="text-slate-400 text-sm">
                {profileLocation ? profileLocation : null}
                {profileLocation && distanceLabel ? " • " : null}
                {distanceLabel ? distanceLabel : null}
              </p>
            ) : null}

            {profileGender ? (
              <p className="text-slate-400 text-xs uppercase tracking-wide">
                {String(profileGender)}
              </p>
            ) : null}

            {/* Intent Signal Badge */}
            {profileIntentSignal && INTENT_SIGNAL_LABELS[profileIntentSignal] && (
              <span 
                className="inline-flex items-center rounded-full bg-purple-500/20 border border-purple-500/40 px-2.5 py-0.5 text-xs font-medium text-purple-200 mt-1"
                data-testid="intent-signal-badge"
              >
                {INTENT_SIGNAL_LABELS[profileIntentSignal]}
              </span>
            )}

            {/* Tap hint */}
            <p className="text-slate-500 text-xs mt-2 flex items-center gap-1">
              <span>👆</span> Tap to view full profile
            </p>
          </div>

          {/* NEW: Relationship Compatibility Score & Explanation */}
          {current?.relationship_compatibility_score !== undefined && current?.relationship_compatibility_score !== null ? (
            <div className="mt-3 bg-rose-900/20 border border-rose-700/50 rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-rose-400 text-lg">💜</span>
                <div className="flex flex-col">
                  <span className="text-rose-300 text-xs font-semibold">
                    {current.relationship_compatibility_score}% Relationship Compatible
                  </span>
                  <span className="text-rose-400/70 text-[10px]">
                    {current.relationship_compatibility_label || 'Based on your preferences'}
                  </span>
                </div>
              </div>
              {current.compatibility_explanation ? (
                <p className="text-rose-200/80 text-xs leading-relaxed mt-1 pl-7">
                  {current.compatibility_explanation}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="mt-2">
            <p className="text-slate-300 text-sm whitespace-pre-line">
              {profileBio || "This person hasn't written a bio yet, but might still be a great match."}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 mt-3">
          <div className="flex justify-between items-center gap-3">
            <button
              type="button"
              onClick={handlePass}
              disabled={!profileUserId || passLoading}
              className="flex-1 inline-flex items-center justify-center rounded-full border border-slate-700 bg-slate-900/60 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {passLoading ? "Passing..." : "Pass"}
            </button>
            <button
              type="button"
              onClick={handleLike}
              disabled={!profileUserId || likeLoading}
              className="flex-1 inline-flex items-center justify-center rounded-full bg-pink-600 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {likeLoading ? "Sending..." : "Send Flirt"}
            </button>
          </div>

          <div className="flex justify-between items-center gap-3">
            <button
              type="button"
              onClick={handlePrev}
              className="flex-1 inline-flex items-center justify-center rounded-full border border-slate-700 bg-slate-900/60 px-4 py-2 text-xs font-medium text-slate-200 hover:bg-slate-800 transition disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={safeIndex === 0}
            >
              Previous
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="flex-1 inline-flex items-center justify-center rounded-full border border-slate-700 bg-slate-900/60 px-4 py-2 text-xs font-medium text-slate-200 hover:bg-slate-800 transition disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={safeIndex >= profiles.length - 1}
            >
              Next
            </button>
          </div>
        </div>

          <p className="text-[11px] text-slate-500 text-center mt-1">
            Browsing at your own pace.{" "}
            <button 
              onClick={() => {
                track("browse_to_quick_match_clicked", { from: "discover_footer" });
                window.location.href = '/quick-match';
              }}
              className="text-rose-400 hover:text-rose-300 underline"
            >
              Try Quick Match (10s timer)
            </button>
          </p>
        </div>
      </div>
      
      {/* Profile View Modal */}
      <ProfileViewModal
        open={profileViewer.open}
        onClose={profileViewer.close}
        profile={profileViewer.profile}
        isSelf={false}
        source="discover"
        onLikeFromProfile={() => {
          profileViewer.close();
          handleLike();
        }}
        onPassFromProfile={() => {
          profileViewer.close();
          handlePass();
        }}
      />
    </div>
  );
}
